/**
 * Additional Licenses and IP Services Content
 */

// ==================== LICENSES ====================

export const isoCertification = {
  id: "iso-certification",
  slug: "iso-certification",
  seoTitle: "ISO Certification India | ISO 9001, 14001, 27001",
  seoDescription: "Get ISO certification for your business. ISO 9001 quality management, ISO 14001 environmental, ISO 27001 security. Expert certification assistance.",
  heroTitle: "ISO Certification",
  heroSubtitle: "Business Certification",
  summary: "ISO (International Organization for Standardization) Certification demonstrates your organization's commitment to quality, environmental responsibility, or information security standards. We help businesses obtain various ISO certifications including ISO 9001, ISO 14001, ISO 27001, and more through accredited certification bodies.",
  
  whoNeeds: {
    title: "Who Needs ISO Certification?",
    description: "Organizations wanting to demonstrate adherence to international standards.",
    points: [
      "Manufacturing companies seeking quality certification",
      "IT companies requiring security certification (ISO 27001)",
      "Exporters needing international credibility",
      "Government tender bidders",
      "Companies in regulated industries",
      "Organizations seeking operational excellence",
      "Businesses expanding to international markets",
      "Companies wanting competitive advantage"
    ]
  },
  
  whyRequired: {
    title: "Why Get ISO Certified?",
    description: "ISO certification provides significant business and operational benefits.",
    points: [
      "Customer Confidence: Demonstrates commitment to quality and standards.",
      "Tender Eligibility: Required for many government and corporate tenders.",
      "Market Access: Opens doors to international markets and clients.",
      "Operational Efficiency: Standardized processes improve efficiency.",
      "Risk Management: Identifies and mitigates operational risks.",
      "Continuous Improvement: Framework for ongoing improvement."
    ]
  },
  
  benefits: [
    { title: "Expert Guidance", description: "Consultants guide through entire certification process." },
    { title: "Documentation", description: "Help prepare all required policies and procedures." },
    { title: "Gap Analysis", description: "Identify gaps between current and required standards." },
    { title: "Implementation", description: "Support implementing required management systems." },
    { title: "Training", description: "Train your team on standard requirements." },
    { title: "Internal Audit", description: "Conduct pre-certification internal audits." },
    { title: "Certification Body", description: "Liaison with accredited certification bodies." },
    { title: "Surveillance Support", description: "Support during annual surveillance audits." }
  ],
  
  documentsRequired: {
    title: "Documents for ISO Certification",
    description: "Documentation requirements depend on ISO standard.",
    directors: [
      "Management commitment letter",
      "Authorized signatory details",
      "Organization structure"
    ],
    company: [
      "Company registration documents",
      "Scope of certification",
      "Existing policies and procedures",
      "Process flow diagrams",
      "Risk assessment documents",
      "Employee training records",
      "Customer feedback data",
      "Performance metrics"
    ]
  },
  
  process: [
    { step: 1, title: "Gap Analysis", description: "Assess current practices against ISO requirements.", duration: "1-2 weeks" },
    { step: 2, title: "Documentation", description: "Develop required policies, procedures, and manuals.", duration: "2-4 weeks" },
    { step: 3, title: "Implementation", description: "Implement management system across organization.", duration: "4-8 weeks" },
    { step: 4, title: "Training", description: "Train employees on new procedures and requirements.", duration: "1-2 weeks" },
    { step: 5, title: "Internal Audit", description: "Conduct internal audit to verify compliance.", duration: "1 week" },
    { step: 6, title: "Certification Audit", description: "External audit by accredited certification body.", duration: "2-5 days" },
    { step: 7, title: "Certificate", description: "Receive ISO certificate valid for 3 years.", duration: "2-4 weeks" }
  ],
  
  timeline: {
    total: "3-6 months",
    breakdown: [
      { phase: "Gap analysis & documentation", duration: "4-6 weeks" },
      { phase: "Implementation", duration: "4-8 weeks" },
      { phase: "Audit & certification", duration: "4-6 weeks" }
    ],
    note: "Timeline depends on organization size and current maturity level."
  },
  
  pricing: {
    title: "ISO Certification Fees",
    description: "Implementation and certification support.",
    packages: [
      {
        name: "Single ISO",
        price: "₹49,999",
        description: "One ISO standard",
        features: [
          "Gap analysis",
          "Documentation support",
          "Implementation guidance",
          "Internal audit",
          "Certification support"
        ]
      },
      {
        name: "Integrated",
        price: "₹84,999",
        description: "Two ISO standards",
        features: [
          "Two certifications",
          "Integrated documentation",
          "Combined implementation",
          "Single audit approach",
          "Cost savings"
        ],
        popular: true
      },
      {
        name: "Enterprise",
        price: "₹1,49,999",
        description: "Three+ standards",
        features: [
          "Multiple certifications",
          "Complete IMS",
          "Dedicated consultant",
          "Training programs",
          "3-year support"
        ]
      }
    ],
    governmentFees: "Certification body fees: ₹50,000-2,00,000 depending on scope and organization size."
  },
  
  faqs: [
    { question: "What are the most common ISO certifications?", answer: "ISO 9001 (Quality), ISO 14001 (Environmental), ISO 27001 (Information Security), ISO 45001 (Occupational Health & Safety), and ISO 22000 (Food Safety)." },
    { question: "How long is ISO certificate valid?", answer: "3 years with annual surveillance audits. Re-certification audit required every 3 years." },
    { question: "Is ISO certification mandatory?", answer: "Not mandatory by law but often required for tenders, exports, and as customer requirement." },
    { question: "What is accredited certification?", answer: "Certification by bodies accredited by national accreditation bodies (NABCB in India). Ensures certificate recognition worldwide." },
    { question: "Can small businesses get ISO certified?", answer: "Yes, ISO standards are scalable. Small businesses can implement proportionate systems based on their size and risk." },
    { question: "What happens in surveillance audit?", answer: "Annual partial audit to verify continued compliance. Covers sample processes and any issues from previous audit." }
  ],
  
  relatedServices: [
    { id: "fssai-license", label: "FSSAI License" },
    { id: "bis-certification", label: "BIS Certification" },
    { id: "trademark-registration", label: "Trademark" },
    { id: "private-limited-company", label: "Company Registration" },
    { id: "virtual-cfo", label: "Virtual CFO" },
    { id: "compliance-manager", label: "Compliance Manager" }
  ],
  
  trustSignals: {
    incorporations: "500+",
    rating: "4.8/5",
    experience: "10+ Years",
    support: "ISO Consultants"
  }
};

// ==================== INTELLECTUAL PROPERTY ====================

export const trademarkRenewal = {
  id: "trademark-renewal",
  slug: "trademark-renewal",
  seoTitle: "Trademark Renewal India | Renew Trademark Online",
  seoDescription: "Renew your trademark before expiry. 10-year trademark renewal process with expert assistance. Protect your brand with timely renewal.",
  heroTitle: "Trademark Renewal",
  heroSubtitle: "Intellectual Property",
  summary: "Trademark registration in India is valid for 10 years from the date of application. To maintain protection, you must renew your trademark before expiry. Renewal can be filed up to 6 months before expiry, or up to 6 months after expiry with additional fees. We ensure timely renewal to protect your brand.",
  
  whoNeeds: {
    title: "Who Needs Trademark Renewal?",
    description: "Trademark owners whose registration is approaching expiry.",
    points: [
      "Businesses with trademarks nearing 10-year mark",
      "Companies with trademarks already expired",
      "Brand owners wanting continued protection",
      "Businesses that acquired trademarks",
      "Franchisors with multiple trademarks",
      "Companies with portfolio of marks",
      "Anyone receiving renewal notice from IP India",
      "Businesses checking trademark status"
    ]
  },
  
  whyRequired: {
    title: "Why Renew Your Trademark?",
    description: "Renewal maintains your exclusive rights over the brand.",
    points: [
      "Continued Protection: Maintain exclusive rights to your mark.",
      "Prevent Cancellation: Unrenewed marks get removed from register.",
      "Brand Security: Competitors cannot register similar marks.",
      "Legal Rights: Continued ability to take infringement action.",
      "Business Value: Trademark is valuable business asset.",
      "Market Position: Protected brand maintains market position."
    ]
  },
  
  benefits: [
    { title: "Expiry Tracking", description: "We track your trademark expiry dates." },
    { title: "Timely Reminders", description: "Advance reminders before expiry." },
    { title: "Documentation", description: "Prepare all renewal documents." },
    { title: "Online Filing", description: "File renewal on IP India portal." },
    { title: "Status Updates", description: "Track renewal application status." },
    { title: "Certificate", description: "Obtain renewed registration certificate." },
    { title: "Portfolio Management", description: "Manage multiple trademark renewals." },
    { title: "Late Renewal", description: "Handle restoration of expired marks." }
  ],
  
  documentsRequired: {
    title: "Documents for Trademark Renewal",
    description: "Minimal documentation for renewal filing.",
    directors: [
      "Applicant identity proof",
      "Authorized signatory details",
      "Power of attorney (if represented)"
    ],
    company: [
      "Trademark registration number",
      "Original registration certificate",
      "Proof of continued use (recommended)",
      "Updated address (if changed)",
      "Company registration (if assignment)"
    ]
  },
  
  process: [
    { step: 1, title: "Status Check", description: "Verify current trademark status and expiry date.", duration: "Same day" },
    { step: 2, title: "Document Preparation", description: "Prepare renewal application and documents.", duration: "1 day" },
    { step: 3, title: "Form Filing", description: "File TM-R on IP India portal.", duration: "Same day" },
    { step: 4, title: "Fee Payment", description: "Pay applicable renewal fees.", duration: "Same day" },
    { step: 5, title: "Processing", description: "IP India processes renewal application.", duration: "3-6 months" },
    { step: 6, title: "Certificate", description: "Receive renewed registration certificate.", duration: "On processing" }
  ],
  
  timeline: {
    total: "3-6 months for processing",
    breakdown: [
      { phase: "Application preparation", duration: "1-2 days" },
      { phase: "Filing", duration: "Same day" },
      { phase: "Processing & certificate", duration: "3-6 months" }
    ],
    note: "File renewal 6 months before expiry. Grace period of 6 months after expiry with surcharge."
  },
  
  pricing: {
    title: "Trademark Renewal Fees",
    description: "Renewal services for trademark protection.",
    packages: [
      {
        name: "Timely Renewal",
        price: "₹5,999",
        description: "Before expiry",
        features: [
          "Application preparation",
          "Online filing",
          "Fee payment",
          "Status tracking",
          "Certificate delivery"
        ]
      },
      {
        name: "Grace Period",
        price: "₹8,999",
        description: "Within 6 months of expiry",
        features: [
          "All Timely features",
          "Surcharge handling",
          "Priority processing",
          "Restoration if needed",
          "Full support"
        ],
        popular: true
      },
      {
        name: "Portfolio",
        price: "₹4,499/mark",
        description: "Multiple marks (5+)",
        features: [
          "Bulk renewal",
          "Portfolio tracking",
          "Expiry calendar",
          "Priority support",
          "Account manager"
        ]
      }
    ],
    governmentFees: "Government fee: ₹9,000 (timely) or ₹18,000 (grace period) for online filing."
  },
  
  faqs: [
    { question: "When should I renew my trademark?", answer: "Renewal can be filed 6 months before expiry date. We recommend filing 3-4 months before expiry to ensure timely processing." },
    { question: "What if my trademark has already expired?", answer: "You have 6 months grace period with additional fees. After that, restoration may be possible but mark may be open for others to register." },
    { question: "Can I renew trademark indefinitely?", answer: "Yes, trademarks can be renewed indefinitely every 10 years as long as renewal fees are paid and mark is in use." },
    { question: "What if trademark ownership changed?", answer: "Assignment must be recorded with IP India before renewal. Renewal filed in name of current owner." },
    { question: "Is proof of use required for renewal?", answer: "Not mandatory for renewal, but recommended to keep. Non-use for 5+ years can lead to cancellation by third parties." },
    { question: "Can expired trademark be restored?", answer: "Yes, within 6-12 months of expiry with restoration request. After that, fresh application needed if mark still available." }
  ],
  
  relatedServices: [
    { id: "trademark-registration", label: "Trademark Registration" },
    { id: "trademark-objection", label: "Trademark Objection" },
    { id: "trademark-opposition", label: "Opposition" },
    { id: "trademark-search", label: "TM Search" },
    { id: "copyright-registration", label: "Copyright" },
    { id: "brand-protection", label: "Brand Protection" }
  ],
  
  trustSignals: {
    incorporations: "10,000+",
    rating: "4.9/5",
    experience: "10+ Years",
    support: "IP Attorneys"
  }
};

export const patentRegistration = {
  id: "patent-registration",
  slug: "patent-registration",
  seoTitle: "Patent Registration India | File Patent Application",
  seoDescription: "Protect your invention with patent registration. Patent search, drafting, filing, and prosecution services. Expert patent attorneys for Indian and PCT patents.",
  heroTitle: "Patent Registration",
  heroSubtitle: "Intellectual Property",
  summary: "Patent Registration protects your inventions by granting exclusive rights to make, use, and sell the invention for 20 years. Patents cover products, processes, and methods that are novel, non-obvious, and industrially applicable. Our patent attorneys handle the complete process from search to grant.",
  
  whoNeeds: {
    title: "Who Should File Patents?",
    description: "Inventors and businesses with novel inventions to protect.",
    points: [
      "Inventors with new products or processes",
      "Technology companies with innovations",
      "Manufacturing companies with new methods",
      "Pharmaceutical companies with new drugs",
      "Startups with innovative solutions",
      "Research institutions commercializing R&D",
      "Individual inventors seeking protection",
      "Companies building IP portfolio"
    ]
  },
  
  whyRequired: {
    title: "Why File a Patent?",
    description: "Patents provide powerful protection for innovations.",
    points: [
      "Exclusive Rights: 20-year monopoly on your invention.",
      "Market Advantage: Prevent competitors from copying.",
      "Licensing Revenue: License patents for royalty income.",
      "Company Valuation: Patents increase business value.",
      "R&D Protection: Protect investment in research.",
      "Investor Appeal: Patents attract investors and funding."
    ]
  },
  
  benefits: [
    { title: "Patentability Search", description: "Prior art search to assess patentability." },
    { title: "Drafting", description: "Technical drafting of patent specification." },
    { title: "Claims", description: "Strategic claim drafting for maximum protection." },
    { title: "Filing", description: "File application with Indian Patent Office." },
    { title: "Prosecution", description: "Handle examination reports and objections." },
    { title: "Grant", description: "Obtain patent grant certificate." },
    { title: "PCT Filing", description: "International patent filing assistance." },
    { title: "Maintenance", description: "Annual renewal and maintenance." }
  ],
  
  documentsRequired: {
    title: "Documents for Patent Filing",
    description: "Technical documentation required for patent application.",
    directors: [
      "Inventor details and declaration",
      "Applicant information",
      "Power of attorney"
    ],
    company: [
      "Detailed invention description",
      "Technical drawings/diagrams",
      "Working examples or data",
      "Prior art references (if known)",
      "Assignment deed (if applicant ≠ inventor)",
      "Priority documents (if claiming priority)"
    ]
  },
  
  process: [
    { step: 1, title: "Disclosure Review", description: "Review invention disclosure and assess patentability.", duration: "3-5 days" },
    { step: 2, title: "Prior Art Search", description: "Search existing patents and publications.", duration: "1-2 weeks" },
    { step: 3, title: "Drafting", description: "Draft patent specification and claims.", duration: "2-4 weeks" },
    { step: 4, title: "Filing", description: "File provisional or complete specification.", duration: "1 day" },
    { step: 5, title: "Publication", description: "Application published after 18 months.", duration: "18 months" },
    { step: 6, title: "Examination", description: "Request examination, respond to reports.", duration: "2-4 years" },
    { step: 7, title: "Grant", description: "Patent granted after successful examination.", duration: "On approval" }
  ],
  
  timeline: {
    total: "3-5 years to grant",
    breakdown: [
      { phase: "Search & drafting", duration: "1-2 months" },
      { phase: "Filing to publication", duration: "18 months" },
      { phase: "Examination to grant", duration: "2-4 years" }
    ],
    note: "Expedited examination available for startups and SMEs. Provisional filing provides early priority date."
  },
  
  pricing: {
    title: "Patent Filing Fees",
    description: "Complete patent services from search to grant.",
    packages: [
      {
        name: "Provisional",
        price: "₹29,999",
        description: "Provisional filing",
        features: [
          "Patentability assessment",
          "Provisional specification",
          "IPO filing",
          "12-month priority",
          "Strategy advice"
        ]
      },
      {
        name: "Complete",
        price: "₹79,999",
        description: "Full patent filing",
        features: [
          "Prior art search",
          "Complete specification",
          "Claims drafting",
          "IPO filing",
          "2 FER responses"
        ],
        popular: true
      },
      {
        name: "End-to-End",
        price: "₹1,49,999",
        description: "Filing to grant",
        features: [
          "All Complete features",
          "All examination responses",
          "Hearing representation",
          "Grant support",
          "5-year renewals"
        ]
      }
    ],
    governmentFees: "Government fees: Startups ₹1,600-4,000, Others ₹4,000-16,000 for filing. Annual renewals additional."
  },
  
  faqs: [
    { question: "What can be patented in India?", answer: "New products, processes, methods, and compositions that are novel, involve inventive step, and are industrially applicable. Software, business methods, and mathematical methods alone are not patentable." },
    { question: "What is the validity of patent?", answer: "20 years from filing date, subject to payment of annual renewal fees." },
    { question: "What is provisional vs complete specification?", answer: "Provisional gives 12-month priority window to file complete. Complete specification with claims is required for examination and grant." },
    { question: "What is PCT filing?", answer: "PCT (Patent Cooperation Treaty) enables filing in 150+ countries through single application. National phase entries needed within 30-31 months." },
    { question: "Can I file patent after public disclosure?", answer: "In India, 12-month grace period from inventor's own disclosure. Best practice is file before any disclosure." },
    { question: "What are startup benefits for patents?", answer: "80% fee reduction, expedited examination, and facilitated processing for DPIIT-recognized startups." }
  ],
  
  relatedServices: [
    { id: "trademark-registration", label: "Trademark" },
    { id: "copyright-registration", label: "Copyright" },
    { id: "design-registration", label: "Design Registration" },
    { id: "startup-india", label: "Startup India" },
    { id: "nda-agreement", label: "NDA" },
    { id: "ip-strategy", label: "IP Strategy" }
  ],
  
  trustSignals: {
    incorporations: "1,000+",
    rating: "4.9/5",
    experience: "15+ Years",
    support: "Patent Attorneys"
  }
};

// ==================== LEGAL DOCUMENTS ====================

export const powerOfAttorney = {
  id: "power-of-attorney",
  slug: "power-of-attorney",
  seoTitle: "Power of Attorney India | POA Drafting & Registration",
  seoDescription: "Professional Power of Attorney drafting and registration. General POA, Special POA, GPA for property. Expert legal services for authorization documents.",
  heroTitle: "Power of Attorney",
  heroSubtitle: "Legal Documents",
  summary: "Power of Attorney (POA) is a legal document that authorizes one person to act on behalf of another in legal, financial, or property matters. Whether you need a General Power of Attorney for broad authority or Special POA for specific transactions, our lawyers draft legally sound documents and assist with registration.",
  
  whoNeeds: {
    title: "Who Needs Power of Attorney?",
    description: "Anyone needing someone else to act on their behalf.",
    points: [
      "NRIs managing property in India",
      "Property owners selling through representative",
      "Business owners delegating authority",
      "Persons unable to attend legal proceedings",
      "Companies authorizing representatives",
      "Elderly persons appointing family members",
      "Patients for medical decisions",
      "Travelers needing someone to act in absence"
    ]
  },
  
  whyRequired: {
    title: "Why is POA Important?",
    description: "POA provides legal authority to act on someone's behalf.",
    points: [
      "Legal Authority: Agent can legally bind principal in transactions.",
      "Convenience: Handle matters without physical presence.",
      "Property Transactions: Essential for sale/purchase through agent.",
      "Business Operations: Delegate signing and decision authority.",
      "Legal Proceedings: Represent in court and legal matters.",
      "Medical Decisions: Authorize medical treatment decisions."
    ]
  },
  
  benefits: [
    { title: "Expert Drafting", description: "Lawyers draft POA with proper clauses and powers." },
    { title: "Custom Powers", description: "Specify exact powers being granted." },
    { title: "Safeguards", description: "Include limitations and safeguards." },
    { title: "Registration", description: "Assist with stamp duty and registration." },
    { title: "Notarization", description: "Notarization for unregistered POAs." },
    { title: "Apostille", description: "Apostille for POAs executed abroad." },
    { title: "Revocation", description: "Draft revocation when POA no longer needed." },
    { title: "Advisory", description: "Advise on type of POA needed." }
  ],
  
  documentsRequired: {
    title: "Documents for POA",
    description: "Basic documentation for Power of Attorney.",
    directors: [
      "Principal's identity proof",
      "Principal's address proof",
      "Agent's identity proof",
      "Agent's address proof",
      "Photographs of both parties"
    ],
    company: [
      "Property documents (for property POA)",
      "Purpose of POA",
      "List of specific powers needed",
      "Duration of POA",
      "Relationship between parties",
      "Previous POA (if revocation)"
    ]
  },
  
  process: [
    { step: 1, title: "Requirement Analysis", description: "Understand purpose and powers needed in POA.", duration: "30 minutes" },
    { step: 2, title: "Drafting", description: "Draft POA with appropriate clauses and powers.", duration: "1-2 days" },
    { step: 3, title: "Review", description: "Principal reviews and approves draft.", duration: "1 day" },
    { step: 4, title: "Stamp Duty", description: "Calculate and pay applicable stamp duty.", duration: "Same day" },
    { step: 5, title: "Execution", description: "Sign in presence of witnesses/notary.", duration: "Same day" },
    { step: 6, title: "Registration", description: "Register at Sub-Registrar if required.", duration: "1-3 days" }
  ],
  
  timeline: {
    total: "3-7 business days",
    breakdown: [
      { phase: "Drafting", duration: "1-2 days" },
      { phase: "Stamp duty & execution", duration: "1-2 days" },
      { phase: "Registration (if needed)", duration: "1-3 days" }
    ],
    note: "Unregistered POA with notarization can be done same day. Registration adds 1-3 days."
  },
  
  pricing: {
    title: "POA Drafting Fees",
    description: "Professional POA services.",
    packages: [
      {
        name: "Special POA",
        price: "₹3,999",
        description: "Specific purpose POA",
        features: [
          "Single transaction/purpose",
          "Expert drafting",
          "Notarization",
          "Two copies",
          "Execution guidance"
        ]
      },
      {
        name: "General POA",
        price: "₹6,999",
        description: "Broad authority POA",
        features: [
          "Multiple powers",
          "Detailed drafting",
          "Stamp duty calculation",
          "Registration support",
          "Complete documentation"
        ],
        popular: true
      },
      {
        name: "Property GPA",
        price: "₹9,999",
        description: "Property transactions",
        features: [
          "Property-specific powers",
          "Sale/purchase authority",
          "Registration included",
          "Title verification",
          "Full legal support"
        ]
      }
    ],
    governmentFees: "Stamp duty: ₹100-1,000 (varies by state). Registration: ₹1,000-5,000 (if registered)."
  },
  
  faqs: [
    { question: "What is difference between General and Special POA?", answer: "General POA grants broad powers for multiple acts. Special POA is limited to specific transaction or purpose mentioned in the document." },
    { question: "Does POA need to be registered?", answer: "POA for immovable property transactions must be registered. Other POAs can be notarized but registration adds authenticity." },
    { question: "Can POA be revoked?", answer: "Yes, principal can revoke POA anytime. Revocation should be documented and communicated to agent and relevant parties." },
    { question: "Is POA valid if principal is abroad?", answer: "Yes, POA can be executed abroad before Indian consulate or notarized and apostilled for use in India." },
    { question: "What is irrevocable POA?", answer: "POA coupled with interest (where agent has interest in subject matter) can be irrevocable. Generally used in business/property transactions." },
    { question: "Does POA survive death?", answer: "No, POA automatically terminates on death of principal. Durable POA can survive incapacity but not death." }
  ],
  
  relatedServices: [
    { id: "agreements-drafting", label: "Agreement Drafting" },
    { id: "affidavit-drafting", label: "Affidavit" },
    { id: "legal-notice", label: "Legal Notice" },
    { id: "property-registration", label: "Property Registration" },
    { id: "will-drafting", label: "Will Drafting" },
    { id: "nda-agreement", label: "NDA" }
  ],
  
  trustSignals: {
    incorporations: "12,000+",
    rating: "4.8/5",
    experience: "10+ Years",
    support: "Expert Lawyers"
  }
};

export const affidavitDrafting = {
  id: "affidavit-drafting",
  slug: "affidavit-drafting",
  seoTitle: "Affidavit Drafting India | Online Affidavit Services",
  seoDescription: "Professional affidavit drafting and notarization. Name change affidavit, address proof, lost document affidavit. Quick online affidavit services.",
  heroTitle: "Affidavit Drafting",
  heroSubtitle: "Legal Documents",
  summary: "An Affidavit is a sworn written statement of facts made voluntarily and confirmed by oath. It's used for various purposes including name change, address proof, lost documents, and court proceedings. Our experts draft legally valid affidavits and assist with notarization for official use.",
  
  whoNeeds: {
    title: "Who Needs Affidavit Services?",
    description: "Anyone requiring sworn statement for official purposes.",
    points: [
      "Persons changing name after marriage",
      "Those declaring correct date of birth",
      "People reporting lost documents",
      "Address proof for various applications",
      "Income declarations for schemes",
      "Court proceedings requiring sworn statements",
      "Gap in employment declarations",
      "Education document corrections"
    ]
  },
  
  whyRequired: {
    title: "Why Are Affidavits Needed?",
    description: "Affidavits provide legally binding sworn declarations.",
    points: [
      "Legal Validity: Sworn statement with legal consequences for false declaration.",
      "Official Requirement: Required by government offices, courts, and institutions.",
      "Proof: Serves as proof when other documents unavailable.",
      "Name Change: Required for official name change process.",
      "Court Cases: Evidence in court proceedings.",
      "Compliance: Meet requirements for various applications."
    ]
  },
  
  benefits: [
    { title: "Expert Drafting", description: "Properly formatted and legally valid affidavits." },
    { title: "Multiple Types", description: "All types of affidavits for various purposes." },
    { title: "Quick Service", description: "Same-day drafting and delivery." },
    { title: "Notarization", description: "Coordination with notary public." },
    { title: "Stamp Paper", description: "Proper stamp paper arrangement." },
    { title: "Correction Free", description: "Error-free professional drafting." },
    { title: "Guidance", description: "Advise on correct affidavit type needed." },
    { title: "Multiple Copies", description: "Provide multiple notarized copies." }
  ],
  
  documentsRequired: {
    title: "Documents for Affidavit",
    description: "Basic information needed for affidavit preparation.",
    directors: [
      "Deponent's identity proof",
      "Address proof",
      "Photograph"
    ],
    company: [
      "Purpose of affidavit",
      "Facts to be declared",
      "Supporting documents (if any)",
      "Reference documents (if correction)",
      "Specific format (if prescribed)"
    ]
  },
  
  process: [
    { step: 1, title: "Requirement", description: "Understand purpose and collect required information.", duration: "15 minutes" },
    { step: 2, title: "Drafting", description: "Draft affidavit in proper legal format.", duration: "1-2 hours" },
    { step: 3, title: "Review", description: "Review draft with client.", duration: "15 minutes" },
    { step: 4, title: "Stamp Paper", description: "Print on appropriate value stamp paper.", duration: "Same day" },
    { step: 5, title: "Notarization", description: "Get notarized by notary public.", duration: "Same day" },
    { step: 6, title: "Delivery", description: "Deliver notarized affidavit.", duration: "Same day" }
  ],
  
  timeline: {
    total: "Same day to 24 hours",
    breakdown: [
      { phase: "Drafting", duration: "1-2 hours" },
      { phase: "Stamp paper", duration: "Same day" },
      { phase: "Notarization", duration: "Same day" }
    ],
    note: "Most affidavits completed same day. Complex affidavits may take 24 hours."
  },
  
  pricing: {
    title: "Affidavit Fees",
    description: "Quick and affordable affidavit services.",
    packages: [
      {
        name: "Standard",
        price: "₹999",
        description: "Common affidavits",
        features: [
          "Name/DOB/Address affidavit",
          "Professional drafting",
          "Stamp paper included",
          "Notarization",
          "2 copies"
        ]
      },
      {
        name: "Special",
        price: "₹1,999",
        description: "Specific purpose",
        features: [
          "Lost document/gap period",
          "Detailed drafting",
          "Multiple facts",
          "3 copies",
          "Express service"
        ],
        popular: true
      },
      {
        name: "Court/Legal",
        price: "₹2,999",
        description: "Court proceedings",
        features: [
          "Court format compliance",
          "Legal review",
          "Multiple pages",
          "5 copies",
          "Urgent delivery"
        ]
      }
    ],
    governmentFees: "Stamp paper: ₹10-100 depending on state. Notary fees: ₹50-200 per document."
  },
  
  faqs: [
    { question: "What are common types of affidavits?", answer: "Name change, date of birth correction, address proof, lost document declaration, income affidavit, gap in education/employment, and general purpose affidavits." },
    { question: "Is notarization mandatory?", answer: "For most official purposes, affidavit must be notarized (signed before notary public). Some courts require oath commissioner authentication." },
    { question: "What stamp paper value is needed?", answer: "Usually ₹10-20 for general affidavits. Value may vary by state and purpose. We advise correct value based on your requirement." },
    { question: "What if affidavit contains false information?", answer: "Making false statement in affidavit is punishable offense under IPC. Can lead to prosecution for perjury and punishment." },
    { question: "Can affidavit be used as address proof?", answer: "Self-declaration affidavit is accepted as address proof by many organizations when supported by other documents." },
    { question: "How long is affidavit valid?", answer: "Affidavits generally don't have expiry. However, some authorities may require recent affidavit (within 6 months or 1 year)." }
  ],
  
  relatedServices: [
    { id: "power-of-attorney", label: "Power of Attorney" },
    { id: "legal-notice", label: "Legal Notice" },
    { id: "agreements-drafting", label: "Agreements" },
    { id: "name-change", label: "Name Change" },
    { id: "notary-services", label: "Notary Services" },
    { id: "nda-agreement", label: "NDA" }
  ],
  
  trustSignals: {
    incorporations: "30,000+",
    rating: "4.9/5",
    experience: "10+ Years",
    support: "Legal Experts"
  }
};

// Export all services
export const additionalLicenseServices = {
  isoCertification
};

export const additionalIPServices = {
  trademarkRenewal,
  patentRegistration
};

export const additionalLegalDocServices = {
  powerOfAttorney,
  affidavitDrafting
};
